/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {WizardService} from './wizard.service';
import { OnInit, Directive } from '@angular/core';
import {WizardStepModel} from './wizard-step.model';

@Directive()
export abstract class WizardStepComponent implements OnInit {
    abstract stepModel: WizardStepModel;
    abstract stepIndex: number;

    protected constructor(protected wizardService: WizardService) {
    }

    /**
     * Snapshot of the last {@link stepModel} state sent to the {@link WizardService}, used by
     * {@link sendUpdateForm} to avoid re-emitting unchanged state.
     * @see sendUpdateForm
     */
    private lastSentStepModelSnapshot: string;

    ngOnInit(): void {
        this.stepModel.isTouched = false;
        this.stepModel.isEmpty = this.isDeepEmpty(this.stepModel.data);
        this.stepModel.index = this.stepIndex;
        this.sendUpdateForm();
    }

    /**
     * Pushes the current {@link stepModel} to the {@link WizardService}.
     * Some subclasses call this method from {@link Component#ngAfterContentChecked}, which runs on every
     * change detection cycle. {@link WizardService#updateStep} synchronously notifies subscribers (e.g.
     * {@link WizardComponent}) that may trigger further change detection, which would re-invoke this method and
     * cause an infinite change-detection feedback loop. Skipping emission when the step model has not actually
     * changed since the last call breaks that loop while still keeping the wizard service state in sync.
     */
    protected sendUpdateForm(): void {
        this.stepModel.isEmpty = this.isDeepEmpty(this.stepModel.data);

        if (this.stepModel == null || this.stepModel.index == null) {
            return;
        }

        const snapshot = JSON.stringify(this.stepModel);
        if (snapshot === this.lastSentStepModelSnapshot) {
            return;
        }
        this.lastSentStepModelSnapshot = snapshot;

        this.wizardService.updateStep(this.stepModel);
    }

    protected isDeepEmpty(object: any): boolean {
        if (object == null) {
            return true;
        }

        if (typeof object === 'string') {
            return object.length === 0;
        }

        if (!(object instanceof Object)) {
            return false;
        }

        const values = Object.values(object);

        if (values.length === 0) {
            return true;
        }

        let isEmpty = true;

        for (const value of values) {
            isEmpty = isEmpty && this.isDeepEmpty(value);

            if (!isEmpty) {
                return false;
            }
        }

        return isEmpty;
    }
}
